<template>
  <SuperAdminShell>
    <div class="super-admin-page">
      <header class="super-admin-page__header">
        <div class="super-admin-page__header-lead">
          <h3>Editar Empresa</h3>
          <small>{{ name || tradeName || 'Atualize os dados e o layout da empresa' }}</small>
        </div>
        <div class="super-admin-page__header-actions">
          <button type="button" class="btnSave btnCancel" @click="$router.push('/admin/empresas')">
            Voltar
          </button>
        </div>
      </header>
      <div class="super-admin-page__shell">
        <div class="super-admin-steps">
          <nav class="super-admin-tabs" aria-label="Seções da empresa">
            <button
              type="button"
              class="super-admin-tab"
              :class="{ 'super-admin-tab--active': activeStep === 0 }"
              @click="setActiveStep(0)"
            >
              <span class="super-admin-tab__index">1</span>
              <span class="super-admin-tab__copy">
                <strong>Relatórios</strong>
                <small>Andamento do cliente</small>
              </span>
            </button>
            <button
              type="button"
              class="super-admin-tab"
              :class="{ 'super-admin-tab--active': activeStep === 1 }"
              @click="setActiveStep(1)"
            >
              <span class="super-admin-tab__index">2</span>
              <span class="super-admin-tab__copy">
                <strong>Dados da Empresa</strong>
                <small>Cadastro e configurações</small>
              </span>
            </button>
            <button
              type="button"
              class="super-admin-tab"
              :class="{ 'super-admin-tab--active': activeStep === 2 }"
              @click="setActiveStep(2)"
            >
              <span class="super-admin-tab__index">3</span>
              <span class="super-admin-tab__copy">
                <strong>Configurações e layout</strong>
                <small>{{ String(typeSystem) === '2' ? 'Personalizar e configurações Weflix' : 'Identidade visual e domínio' }}</small>
              </span>
            </button>
          </nav>

          <div
            class="super-admin-page__panel super-admin-page__panel--tabbed sa-company-progress-tab"
            v-show="hasReady && activeStep === 0"
          >
            <CompanyProgressPanel
              :metrics="companyOverview.metrics"
              :checklist="companyOverview.checklist"
              :charts="companyOverview.charts"
              :company-name="companyOverview.companyName || name || tradeName"
              :company-id="id"
              :is-mock="companyOverview.isMock"
            />
          </div>

          <div class="super-admin-page__panel super-admin-page__panel--tabbed" v-show="hasReady && activeStep === 1">
            <div class="section-title">
              <h4>Informações da Empresa</h4>
              <p>Dados básicos da empresa e do recebedor Pagar.me</p>
              <p class="sa-field-subtle">
                Campos com <fa icon="fa-solid fa-circle-exclamation"></fa> explicam regras do recebedor / compras — passe o mouse para ver.
              </p>
            </div>

            <div class="sa-company-integrations" v-if="id">
              <p class="sa-company-integrations__status">
                <span>Pagar.me: {{ recipientCode ? recipientCode : 'sem recebedor' }}</span>
                <span>Videoteca pasta: {{ videotecaFolderLabel }}</span>
                <span>Videoteca token: {{ videotecaTokenLabel }}</span>
              </p>
              <div class="sa-company-integrations__actions">
                <button
                  type="button"
                  class="btnSave btnCancel"
                  :disabled="retryingPagarme || !hasReady"
                  @click="retryRecipient"
                >
                  {{ retryingPagarme ? 'Atualizando...' : (recipientCode ? 'Atualizar recebedor Pagar.me' : 'Criar recebedor Pagar.me') }}
                </button>
                <button
                  type="button"
                  class="btnSave btnCancel"
                  :disabled="retryingVideoteca || !hasReady"
                  @click="retryVideoteca"
                >
                  {{ retryingVideoteca ? 'Criando pasta...' : 'Criar pasta Videoteca' }}
                </button>
              </div>
            </div>
            <div class="row">
              <div class="col-form-inner col-6 col-md-6 col-lg-6">
                <div class="form-group">
                  <div class="form-label">
                    <label>
                      Razão Social
                      <v-tooltip :text="fieldTip('tradeName')" location="top">
                        <template v-slot:activator="{ props }">
                          <fa v-bind="props" icon="fa-solid fa-circle-exclamation"></fa>
                        </template>
                      </v-tooltip>
                    </label>
                  </div>
                  <div class="form-input">
                    <input
                      type="text"
                      required
                      class="form-control"
                      v-model="tradeName"
                      placeholder="Digite a razão social"
                      :disabled="kycLocked"
                    />
                  </div>
                </div>
              </div>

              <div class="col-form-inner col-6 col-md-6 col-lg-6">
                <div class="form-group">
                  <div class="form-label">
                    <label>
                      Nome Fantasia
                      <v-tooltip :text="fieldTip('name')" location="top">
                        <template v-slot:activator="{ props }">
                          <fa v-bind="props" icon="fa-solid fa-circle-exclamation"></fa>
                        </template>
                      </v-tooltip>
                    </label>
                  </div>
                  <div class="form-input">
                    <input
                      type="text"
                      required
                      class="form-control"
                      v-model="name"
                      placeholder="Digite o nome fantasia"
                      :disabled="kycLocked"
                    />
                  </div>
                </div>
              </div>

              <div class="col-form-inner col-4 col-md-4 col-lg-4">
                <div class="form-group">
                  <div class="form-label">
                    <label>
                      CNPJ
                      <v-tooltip :text="fieldTip('idFiscal')" location="top">
                        <template v-slot:activator="{ props }">
                          <fa v-bind="props" icon="fa-solid fa-circle-exclamation"></fa>
                        </template>
                      </v-tooltip>
                    </label>
                  </div>
                  <div class="form-input">
                    <input
                      type="text"
                      required
                      class="form-control"
                      v-model="idFiscal"
                      placeholder="Digite o CNPJ"
                      v-mask="'##.###.###/####-##'"
                      :disabled="kycLocked"
                    />
                  </div>
                </div>
              </div>

              <div class="col-form-inner col-6 col-md-6 col-lg-6">
                <div class="form-group">
                  <div class="form-label">
                    <label>
                      E-mail
                      <v-tooltip :text="fieldTip('email')" location="top">
                        <template v-slot:activator="{ props }">
                          <fa v-bind="props" icon="fa-solid fa-circle-exclamation"></fa>
                        </template>
                      </v-tooltip>
                    </label>
                  </div>
                  <div class="form-input">
                    <input
                      type="text"
                      required
                      class="form-control"
                      v-model="email"
                      placeholder="Digite o e-mail"
                      :disabled="kycLocked"
                    />
                  </div>
                </div>
              </div>

              <div class="col-form-inner col-2 col-md-2 col-lg-2">
                <div class="form-group">
                  <div class="form-label">
                    <label>
                      Telefone
                      <v-tooltip :text="fieldTip('phone')" location="top">
                        <template v-slot:activator="{ props }">
                          <fa v-bind="props" icon="fa-solid fa-circle-exclamation"></fa>
                        </template>
                      </v-tooltip>
                    </label>
                  </div>
                  <div class="form-input">
                    <input
                      type="text"
                      required
                      class="form-control"
                      v-model="phone"
                      placeholder="Digite o telefone"
                      v-mask="'(##) #####-####'"
                      :disabled="kycLocked"
                    />
                  </div>
                </div>
              </div>

              <!-- Seção: Endereço -->
              <div class="col-form-inner col-12 col-md-12 col-lg-12">
                <div class="section-title">
                  <h4>Endereço</h4>
                  <p>Endereço e telefone do responsável reusam os da empresa.</p>
                </div>
              </div>
              <div class="col-form-inner col-2 col-md-2 col-lg-2">
                <div class="form-group">
                  <div class="form-label">
                    <label>
                      CEP
                      <v-tooltip :text="fieldTip('postcode')" location="top">
                        <template v-slot:activator="{ props }">
                          <fa v-bind="props" icon="fa-solid fa-circle-exclamation"></fa>
                        </template>
                      </v-tooltip>
                    </label>
                  </div>
                  <div class="form-input">
                    <input
                      type="text"
                      class="form-control"
                      placeholder="00000-000"
                      v-model="postcode"
                      v-mask="'#####-###'"
                      @input="getCep"
                      :disabled="kycLocked"
                    />
                  </div>
                </div>
              </div>

              <div class="col-form-inner col-8 col-md-8 col-lg-8">
                <div class="form-group">
                  <div class="form-label">
                    <label>
                      Endereço
                      <v-tooltip :text="fieldTip('address')" location="top">
                        <template v-slot:activator="{ props }">
                          <fa v-bind="props" icon="fa-solid fa-circle-exclamation"></fa>
                        </template>
                      </v-tooltip>
                    </label>
                  </div>
                  <div class="form-input">
                    <input
                      type="text"
                      class="form-control"
                      v-model="address"
                      placeholder="Digite o endereço"
                      :disabled="kycLocked"
                    />
                  </div>
                </div>
              </div>

              <div class="col-form-inner col-2 col-md-2 col-lg-2">
                <div class="form-group">
                  <div class="form-label">
                    <label>
                      Número
                      <v-tooltip :text="fieldTip('addressNumber')" location="top">
                        <template v-slot:activator="{ props }">
                          <fa v-bind="props" icon="fa-solid fa-circle-exclamation"></fa>
                        </template>
                      </v-tooltip>
                    </label>
                  </div>
                  <div class="form-input">
                    <input
                      type="text"
                      class="form-control"
                      v-model="addressNumber"
                      placeholder="Nº"
                      :disabled="kycLocked"
                    />
                  </div>
                </div>
              </div>

              <div class="col-form-inner col">
                <div class="form-group">
                  <div class="form-label">
                    <label>
                      Complemento
                      <v-tooltip :text="fieldTip('addressComplement')" location="top">
                        <template v-slot:activator="{ props }">
                          <fa v-bind="props" icon="fa-solid fa-circle-exclamation"></fa>
                        </template>
                      </v-tooltip>
                    </label>
                  </div>
                  <div class="form-input">
                    <input
                      type="text"
                      class="form-control"
                      v-model="addressComplement"
                      placeholder="Se vazio, Weeduc envia “Sem complemento”"
                      :disabled="kycLocked"
                    />
                  </div>
                </div>
              </div>

              <div class="col-form-inner col">
                <div class="form-group">
                  <div class="form-label">
                    <label>
                      Bairro
                      <v-tooltip :text="fieldTip('neighborhood')" location="top">
                        <template v-slot:activator="{ props }">
                          <fa v-bind="props" icon="fa-solid fa-circle-exclamation"></fa>
                        </template>
                      </v-tooltip>
                    </label>
                  </div>
                  <div class="form-input">
                    <input
                      type="text"
                      class="form-control"
                      v-model="neighborhood"
                      placeholder="Digite o bairro"
                      :disabled="kycLocked"
                    />
                  </div>
                </div>
              </div>

              <div class="col-form-inner col">
                <div class="form-group">
                  <div class="form-label">
                    <label>
                      Cidade
                      <v-tooltip :text="fieldTip('city')" location="top">
                        <template v-slot:activator="{ props }">
                          <fa v-bind="props" icon="fa-solid fa-circle-exclamation"></fa>
                        </template>
                      </v-tooltip>
                    </label>
                  </div>
                  <div class="form-input">
                    <input
                      type="text"
                      class="form-control"
                      v-model="city"
                      placeholder="Digite a cidade"
                      :disabled="kycLocked"
                    />
                  </div>
                </div>
              </div>

              <div class="col-form-inner col">
                <div class="form-group">
                  <div class="form-label">
                    <label>
                      UF
                      <v-tooltip :text="fieldTip('county')" location="top">
                        <template v-slot:activator="{ props }">
                          <fa v-bind="props" icon="fa-solid fa-circle-exclamation"></fa>
                        </template>
                      </v-tooltip>
                    </label>
                  </div>
                  <div class="form-input">
                    <input
                      type="text"
                      class="form-control"
                      v-model="county"
                      placeholder="UF"
                      maxlength="2"
                      :disabled="kycLocked"
                    />
                  </div>
                </div>
              </div>

              <div class="col-form-inner col">
                <div class="form-group">
                  <div class="form-label">
                    <label>
                      País
                      <v-tooltip :text="fieldTip('country')" location="top">
                        <template v-slot:activator="{ props }">
                          <fa v-bind="props" icon="fa-solid fa-circle-exclamation"></fa>
                        </template>
                      </v-tooltip>
                    </label>
                  </div>
                  <div class="form-input">
                    <input
                      type="text"
                      class="form-control"
                      v-model="country"
                      placeholder="Digite o país"
                    />
                  </div>
                </div>
              </div>

              <!-- Seção: Configurações do Sistema -->
              <div class="col-form-inner col-12 col-md-12 col-lg-12">
                <div class="section-title">
                  <h4>Configurações do Sistema</h4>
                  <p>Configurações da plataforma (não vão à Pagar.me).</p>
                </div>
              </div>
              <div class="col-form-inner col-3 col-md-3 col-lg-3">
                <div class="form-group">
                  <div class="form-label">
                    <label>
                      Tipo de Sistema
                      <v-tooltip :text="fieldTip('typeSystem')" location="top">
                        <template v-slot:activator="{ props }">
                          <fa v-bind="props" icon="fa-solid fa-circle-exclamation"></fa>
                        </template>
                      </v-tooltip>
                    </label>
                  </div>
                  <div class="form-input">
                    <select class="form-control" v-model="typeSystem">
                      <option value="" disabled>Selecione o tipo</option>
                      <option value="1">Weeduc</option>
                      <option value="2">Weflix</option>
                    </select>
                  </div>
                </div>
              </div>

              <div class="col-form-inner col-3 col-md-3 col-lg-3" v-if="String(typeSystem) === '1'">
                <div class="form-group">
                  <div class="form-label">
                    <label>
                      Tipo de Empresa
                      <v-tooltip :text="fieldTip('typeBusiness')" location="top">
                        <template v-slot:activator="{ props }">
                          <fa v-bind="props" icon="fa-solid fa-circle-exclamation"></fa>
                        </template>
                      </v-tooltip>
                    </label>
                  </div>
                  <div class="form-input">
                    <select class="form-control" v-model="typeBusiness">
                      <option value="" disabled>Selecione o tipo</option>
                      <option value="1">Escolar</option>
                      <option value="2">Corporativo</option>
                    </select>
                  </div>
                </div>
              </div>

              <div class="col-form-inner col-3 col-md-3 col-lg-3" v-if="String(typeSystem) === '2'">
                <div class="form-group">
                  <div class="form-label">
                    <label>
                      Plano de Teste
                      <v-tooltip :text="fieldTip('testPlan')" location="top">
                        <template v-slot:activator="{ props }">
                          <fa v-bind="props" icon="fa-solid fa-circle-exclamation"></fa>
                        </template>
                      </v-tooltip>
                    </label>
                  </div>
                  <div class="form-input">
                    <select class="form-control" v-model.number="testPlan">
                      <option :value="1">Sim</option>
                      <option :value="0">Não</option>
                    </select>
                  </div>
                </div>
              </div>

              <div class="col-form-inner col-3 col-md-3 col-lg-3" v-if="String(typeSystem) === '2'">
                <div class="form-group">
                  <div class="form-label">
                    <label>
                      Comentários em Vídeo
                      <v-tooltip :text="fieldTip('videoComments')" location="top">
                        <template v-slot:activator="{ props }">
                          <fa v-bind="props" icon="fa-solid fa-circle-exclamation"></fa>
                        </template>
                      </v-tooltip>
                    </label>
                  </div>
                  <div class="form-input">
                    <select class="form-control" v-model.number="videoComments">
                      <option :value="1">Ativo</option>
                      <option :value="0">Inativo</option>
                    </select>
                  </div>
                </div>
              </div>

              <div class="col-form-inner col-3 col-md-3 col-lg-3">
                <div class="form-group">
                  <div class="form-label">
                    <label>
                      Restrição de Idade
                      <v-tooltip :text="fieldTip('ageRestricted')" location="top">
                        <template v-slot:activator="{ props }">
                          <fa v-bind="props" icon="fa-solid fa-circle-exclamation"></fa>
                        </template>
                      </v-tooltip>
                    </label>
                  </div>
                  <div class="form-input">
                    <select class="form-control" v-model.number="ageRestricted">
                      <option :value="1">Sim</option>
                      <option :value="0">Não</option>
                    </select>
                  </div>
                </div>
              </div>

              <div class="col-form-inner col-3 col-md-3 col-lg-3">
                <div class="form-group">
                  <div class="form-label">
                    <label>
                      Tempo Total de Vídeo
                      <v-tooltip :text="fieldTip('videoTimeTotal')" location="top">
                        <template v-slot:activator="{ props }">
                          <fa v-bind="props" icon="fa-solid fa-circle-exclamation"></fa>
                        </template>
                      </v-tooltip>
                    </label>
                  </div>
                  <div class="form-input">
                    <input
                      type="number"
                      class="form-control"
                      v-model="videoTimeTotal"
                      placeholder="Tempo total (min)"
                    />
                  </div>
                </div>
              </div>

              <div class="col-form-inner col-3 col-md-3 col-lg-3">
                <div class="form-group">
                  <div class="form-label">
                    <label>
                      Tempo Usado de Vídeo
                      <v-tooltip :text="fieldTip('videoTimeUsed')" location="top">
                        <template v-slot:activator="{ props }">
                          <fa v-bind="props" icon="fa-solid fa-circle-exclamation"></fa>
                        </template>
                      </v-tooltip>
                    </label>
                  </div>
                  <div class="form-input">
                    <input
                      type="number"
                      class="form-control"
                      v-model="videoTimeUsed"
                      placeholder="Tempo usado (min)"
                    />
                  </div>
                </div>
              </div>

              <div class="col-form-inner col-3 col-md-3 col-lg-3">
                <div class="form-group">
                  <div class="form-label">
                    <label>
                      Haverá vendas?
                      <v-tooltip text="Não: dados bancários, split, carrinho, antecipação e parcelamento ficam ocultos." location="top">
                        <template v-slot:activator="{ props }">
                          <fa v-bind="props" icon="fa-solid fa-circle-exclamation"></fa>
                        </template>
                      </v-tooltip>
                    </label>
                  </div>
                  <div class="form-input">
                    <select class="form-control" v-model="hasSales" @change="onSalesIntentChange">
                      <option value="" disabled>Selecione</option>
                      <option value="1">Sim</option>
                      <option value="0">Não</option>
                    </select>
                  </div>
                </div>
              </div>

              <div class="col-form-inner col-3 col-md-3 col-lg-3">
                <div class="form-group">
                  <div class="form-label">
                    <label>
                      Token da conta Videoteca
                      <v-tooltip :text="fieldTip('tokenVideoteca')" location="top">
                        <template v-slot:activator="{ props }">
                          <fa v-bind="props" icon="fa-solid fa-circle-exclamation"></fa>
                        </template>
                      </v-tooltip>
                    </label>
                  </div>
                  <div class="form-input">
                    <input
                      type="text"
                      class="form-control"
                      v-model="tokenVideoteca"
                      placeholder="Token da conta Videoteca (opcional)"
                      autocomplete="off"
                    />
                  </div>
                </div>
              </div>

              <div class="col-form-inner col-3 col-md-3 col-lg-3">
                <div class="form-group">
                  <div class="form-label">
                    <label>
                      Código Recebedor
                      <v-tooltip :text="fieldTip('recipientCode')" location="top">
                        <template v-slot:activator="{ props }">
                          <fa v-bind="props" icon="fa-solid fa-circle-exclamation"></fa>
                        </template>
                      </v-tooltip>
                    </label>
                  </div>
                  <div class="form-input">
                    <input
                      type="text"
                      class="form-control"
                      v-model="recipientCode"
                      placeholder="Opcional (gerado pela Pagar.me)"
                    />
                  </div>
                </div>
              </div>

              <!-- Seção: Pagamento -->
              <template v-if="String(hasSales) === '1' && Number(cartStatus) !== 2">
              <div class="col-form-inner col-12 col-md-12 col-lg-12">
                <div class="section-title">
                  <h4>Informações de pagamento</h4>
                  <p>Carrinho, antecipação e parcelamento só valem quando há vendas.</p>
                </div>
              </div>
              <div class="col-form-inner col-3 col-md-3 col-lg-3">
                <div class="form-group">
                  <div class="form-label">
                    <label>
                      Status do Carrinho
                      <v-tooltip :text="fieldTip('cartStatus')" location="top">
                        <template v-slot:activator="{ props }">
                          <fa v-bind="props" icon="fa-solid fa-circle-exclamation"></fa>
                        </template>
                      </v-tooltip>
                    </label>
                  </div>
                  <div class="form-input">
                    <select class="form-control" v-model="cartStatus">
                      <option :value="1">Ativo</option>
                      <option :value="0">Inativo</option>
                    </select>
                  </div>
                </div>
              </div>
              <div class="col-form-inner col-3 col-md-3 col-lg-3">
                <div class="form-group">
                  <div class="form-label">
                    <label>
                      Antecipação
                      <v-tooltip :text="fieldTip('anticipation')" location="top">
                        <template v-slot:activator="{ props }">
                          <fa v-bind="props" icon="fa-solid fa-circle-exclamation"></fa>
                        </template>
                      </v-tooltip>
                    </label>
                  </div>
                  <div class="form-input">
                    <select class="form-control" v-model.number="anticipation">
                      <option :value="1">Ativo</option>
                      <option :value="0">Inativo</option>
                    </select>
                  </div>
                </div>
              </div>
              <div class="col-form-inner col-3 col-md-3 col-lg-3">
                <div class="form-group">
                  <div class="form-label">
                    <label>
                      Parcelamento no Checkout
                      <v-tooltip :text="fieldTip('intallmentCheckout')" location="top">
                        <template v-slot:activator="{ props }">
                          <fa v-bind="props" icon="fa-solid fa-circle-exclamation"></fa>
                        </template>
                      </v-tooltip>
                    </label>
                  </div>
                  <div class="form-input">
                    <select class="form-control" v-model.number="intallmentCheckout">
                      <option :value="1">Ativo</option>
                      <option :value="0">Inativo</option>
                    </select>
                  </div>
                </div>
              </div>
              <div class="col-form-inner col-12 col-md-12 col-lg-12">
                <div class="section-title">
                  <h4>Dados Bancários e Split</h4>
                  <p>Dados bancários do recebedor.</p>
                  <p class="sa-field-subtle">
                    A conta deve estar no nome da empresa e no mesmo CNPJ cadastrado — outro CPF/CNPJ é rejeitado pela Pagar.me.
                  </p>
                </div>
              </div>
              <div class="col-form-inner col-2 col-md-2 col-lg-2">
                <div class="form-group">
                  <div class="form-label">
                    <label>
                      Banco
                      <v-tooltip :text="fieldTip('bank')" location="top">
                        <template v-slot:activator="{ props }">
                          <fa v-bind="props" icon="fa-solid fa-circle-exclamation"></fa>
                        </template>
                      </v-tooltip>
                    </label>
                  </div>
                  <div class="form-input">
                    <input
                      type="text"
                      class="form-control"
                      v-model="bank"
                      placeholder="Ex: 341"
                    />
                  </div>
                </div>
              </div>

              <div class="col-form-inner col-2 col-md-2 col-lg-2">
                <div class="form-group">
                  <div class="form-label">
                    <label>
                      Agência
                      <v-tooltip :text="fieldTip('branch')" location="top">
                        <template v-slot:activator="{ props }">
                          <fa v-bind="props" icon="fa-solid fa-circle-exclamation"></fa>
                        </template>
                      </v-tooltip>
                    </label>
                  </div>
                  <div class="form-input">
                    <input
                      type="text"
                      class="form-control"
                      v-model="branch"
                      placeholder="Número da agência"
                    />
                  </div>
                </div>
              </div>

              <div class="col-form-inner col-2 col-md-2 col-lg-2">
                <div class="form-group">
                  <div class="form-label">
                    <label>
                      Dígito da Agência
                      <v-tooltip :text="fieldTip('digitBranch')" location="top">
                        <template v-slot:activator="{ props }">
                          <fa v-bind="props" icon="fa-solid fa-circle-exclamation"></fa>
                        </template>
                      </v-tooltip>
                    </label>
                  </div>
                  <div class="form-input">
                    <input
                      type="text"
                      class="form-control"
                      v-model="digitBranch"
                      placeholder="Dígito"
                    />
                  </div>
                </div>
              </div>

              <div class="col-form-inner col-3 col-md-3 col-lg-3">
                <div class="form-group">
                  <div class="form-label">
                    <label>
                      Conta
                      <v-tooltip :text="fieldTip('account')" location="top">
                        <template v-slot:activator="{ props }">
                          <fa v-bind="props" icon="fa-solid fa-circle-exclamation"></fa>
                        </template>
                      </v-tooltip>
                    </label>
                  </div>
                  <div class="form-input">
                    <input
                      type="text"
                      class="form-control"
                      v-model="account"
                      placeholder="Número da conta"
                    />
                  </div>
                </div>
              </div>

              <div class="col-form-inner col-3 col-md-3 col-lg-3">
                <div class="form-group">
                  <div class="form-label">
                    <label>
                      Dígito da Conta
                      <v-tooltip :text="fieldTip('digitAccount')" location="top">
                        <template v-slot:activator="{ props }">
                          <fa v-bind="props" icon="fa-solid fa-circle-exclamation"></fa>
                        </template>
                      </v-tooltip>
                    </label>
                  </div>
                  <div class="form-input">
                    <input
                      type="text"
                      class="form-control"
                      v-model="digitAccount"
                      placeholder="Dígito"
                    />
                  </div>
                </div>
              </div>

              <div class="col-form-inner col">
                <div class="form-group">
                  <div class="form-label">
                    <label>
                      Split
                      <v-tooltip :text="fieldTip('split')" location="top">
                        <template v-slot:activator="{ props }">
                          <fa v-bind="props" icon="fa-solid fa-circle-exclamation"></fa>
                        </template>
                      </v-tooltip>
                    </label>
                  </div>
                  <div class="form-input">
                    <select class="form-control" v-model="split" required>
                      <option value="" disabled>Selecione</option>
                      <option :value="1">Ativo</option>
                      <option :value="0">Inativo</option>
                    </select>
                  </div>
                </div>
              </div>
              <div class="col-form-inner col">
                <div class="form-group">
                  <div class="form-label">
                    <label>
                      Percentual do Cliente
                      <v-tooltip :text="fieldTip('percentualClient')" location="top">
                        <template v-slot:activator="{ props }">
                          <fa v-bind="props" icon="fa-solid fa-circle-exclamation"></fa>
                        </template>
                      </v-tooltip>
                    </label>
                  </div>
                  <div class="form-input">
                    <input
                      type="number"
                      step="0.01"
                      class="form-control"
                      v-model="percentualClient"
                      placeholder="Percentual (%)"
                      required
                    />
                  </div>
                </div>
              </div>

              <div class="col-form-inner col">
                <div class="form-group">
                  <div class="form-label">
                    <label>
                      Taxa / Valor da Transação
                      <v-tooltip :text="fieldTip('transactionValue')" location="top">
                        <template v-slot:activator="{ props }">
                          <fa v-bind="props" icon="fa-solid fa-circle-exclamation"></fa>
                        </template>
                      </v-tooltip>
                    </label>
                  </div>
                  <div class="form-input">
                    <input
                      type="number"
                      step="0.01"
                      class="form-control"
                      v-model="transactionValue"
                      placeholder="Valor (R$)"
                      required
                    />
                  </div>
                </div>
              </div>
              </template>

              <!-- Seção: Informações do Responsável -->
              <div class="col-form-inner col-12 col-md-12 col-lg-12">
                <div class="section-title">
                  <h4>Informações do Responsável</h4>
                  <p>Responsável legal.</p>
                </div>
              </div>
              <div class="col-form-inner col-6 col-md-6 col-lg-6">
                <div class="form-group">
                  <div class="form-label">
                    <label>
                      Nome do Responsável
                      <v-tooltip :text="fieldTip('nameRepresentative')" location="top">
                        <template v-slot:activator="{ props }">
                          <fa v-bind="props" icon="fa-solid fa-circle-exclamation"></fa>
                        </template>
                      </v-tooltip>
                    </label>
                  </div>
                  <div class="form-input">
                    <input
                      type="text"
                      class="form-control"
                      v-model="nameRepresentative"
                      placeholder="João da Silva"
                    />
                  </div>
                </div>
              </div>
              <div class="col-form-inner col-6 col-md-6 col-lg-6">
                <div class="form-group">
                  <div class="form-label">
                    <label>
                      E-mail do Responsável
                      <v-tooltip :text="fieldTip('emailRepresentative')" location="top">
                        <template v-slot:activator="{ props }">
                          <fa v-bind="props" icon="fa-solid fa-circle-exclamation"></fa>
                        </template>
                      </v-tooltip>
                    </label>
                  </div>
                  <div class="form-input">
                    <input
                      type="email"
                      class="form-control"
                      v-model="emailRepresentative"
                      placeholder="email@email.com"
                    />
                  </div>
                </div>
              </div>
              <div class="col-form-inner col-4 col-md-4 col-lg-4">
                <div class="form-group">
                  <div class="form-label">
                    <label>
                      CPF do Responsável
                      <v-tooltip :text="fieldTip('documentRepresentative')" location="top">
                        <template v-slot:activator="{ props }">
                          <fa v-bind="props" icon="fa-solid fa-circle-exclamation"></fa>
                        </template>
                      </v-tooltip>
                    </label>
                  </div>
                  <div class="form-input">
                    <input
                      type="text"
                      class="form-control"
                      v-model="documentRepresentative"
                      v-mask="'###.###.###-##'"
                      placeholder="000.000.000-00"
                    />
                  </div>
                </div>
              </div>
              <div class="col-form-inner col-4 col-md-4 col-lg-4">
                <div class="form-group">
                  <div class="form-label">
                    <label>
                      Data de Nascimento
                      <v-tooltip :text="fieldTip('birthdateRepresentative')" location="top">
                        <template v-slot:activator="{ props }">
                          <fa v-bind="props" icon="fa-solid fa-circle-exclamation"></fa>
                        </template>
                      </v-tooltip>
                    </label>
                  </div>
                  <div class="form-input">
                    <input
                      type="date"
                      class="form-control"
                      v-model="birthdateRepresentative"
                    />
                  </div>
                </div>
              </div>
              <div class="col-form-inner col-4 col-md-4 col-lg-4">
                <div class="form-group">
                  <div class="form-label">
                    <label>
                      Profissão do Responsável
                      <v-tooltip :text="fieldTip('occupationRepresentative')" location="top">
                        <template v-slot:activator="{ props }">
                          <fa v-bind="props" icon="fa-solid fa-circle-exclamation"></fa>
                        </template>
                      </v-tooltip>
                    </label>
                  </div>
                  <div class="form-input">
                    <input
                      type="text"
                      class="form-control"
                      v-model="occupationRepresentative"
                      placeholder="Ex.: Engenheiro"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div class="sa-company-security">
              <div class="section-title">
                <h4>Segurança</h4>
                <p>Defina o acesso de alunos e gestores, e quando um IP deve ser bloqueado por excesso de tentativas.</p>
              </div>

              <div class="sa-security-tabs" role="tablist" aria-label="Papel">
                <button
                  type="button"
                  role="tab"
                  class="sa-security-tabs__tab"
                  :class="{ 'is-active': securityRoleTab === 'student' }"
                  :aria-selected="securityRoleTab === 'student'"
                  @click="securityRoleTab = 'student'"
                >
                  Aluno
                </button>
                <button
                  type="button"
                  role="tab"
                  class="sa-security-tabs__tab"
                  :class="{ 'is-active': securityRoleTab === 'manager' }"
                  :aria-selected="securityRoleTab === 'manager'"
                  @click="securityRoleTab = 'manager'"
                >
                  Gestor
                </button>
              </div>

              <section v-show="securityRoleTab === 'student'" class="sa-security-role" role="tabpanel">
                <div class="sa-security-list">
                  <div class="sa-security-row">
                    <div>
                      <strong>Login via e-mail</strong>
                      <p>O aluno entra com um link enviado por e-mail, sem senha. Essa opção é independente da verificação em duas etapas.</p>
                    </div>
                    <label class="sa-security-switch">
                      <input type="checkbox" v-model="emailLinkLogin" />
                      <span class="sa-security-switch__track" aria-hidden="true"></span>
                      <span class="sa-security-switch__text">{{ emailLinkLogin ? 'Ativo' : 'Inativo' }}</span>
                    </label>
                  </div>
                  <div class="sa-security-row">
                    <div>
                      <strong>2FA obrigatório</strong>
                      <p>Por padrão fica desligado. Ativado, o login com senha exige o método abaixo. Desativado, o aluno entra só com a senha e pode ligar o 2FA só na conta dele. Ao desligar, o método volta para o código por e-mail.</p>
                    </div>
                    <label class="sa-security-switch">
                      <input type="checkbox" :checked="studentTwoFactorRequired" @change="setStudentTwoFactor($event.target.checked)" />
                      <span class="sa-security-switch__track" aria-hidden="true"></span>
                      <span class="sa-security-switch__text">{{ studentTwoFactorRequired ? 'Ativo' : 'Inativo' }}</span>
                    </label>
                  </div>
                </div>

                <fieldset v-if="studentTwoFactorRequired" class="sa-security-methods">
                  <legend>Forma de confirmação</legend>
                  <p>Marque o aplicativo, o código por e-mail, ou os dois.</p>
                  <label
                    class="sa-security-method"
                    :class="{ 'is-selected': hasSecurityMethod(twoFactorMethod, 'authenticator') }"
                  >
                    <input
                      type="checkbox"
                      :checked="hasSecurityMethod(twoFactorMethod, 'authenticator')"
                      @change="toggleStudentMethod('authenticator', $event.target.checked, $event)"
                    />
                    <span>
                      <strong>Google Authenticator</strong>
                      <small>O aluno escaneia um QR code no aplicativo Google Authenticator.</small>
                    </span>
                  </label>
                  <label
                    class="sa-security-method"
                    :class="{ 'is-selected': hasSecurityMethod(twoFactorMethod, 'email') }"
                  >
                    <input
                      type="checkbox"
                      :checked="hasSecurityMethod(twoFactorMethod, 'email')"
                      @change="toggleStudentMethod('email', $event.target.checked, $event)"
                    />
                    <span>
                      <strong>Código por e-mail</strong>
                      <small>O aluno recebe um código de 6 dígitos por e-mail.</small>
                    </span>
                  </label>
                </fieldset>
              </section>

              <section v-show="securityRoleTab === 'manager'" class="sa-security-role" role="tabpanel">
                <div class="sa-security-list">
                  <div class="sa-security-row">
                    <div>
                      <strong>2FA obrigatório</strong>
                      <p>A verificação em duas etapas do gestor fica sempre ativa.</p>
                    </div>
                    <label class="sa-security-switch is-locked" aria-disabled="true">
                      <input type="checkbox" checked disabled />
                      <span class="sa-security-switch__track" aria-hidden="true"></span>
                      <span class="sa-security-switch__text">Sempre ativo</span>
                    </label>
                  </div>
                </div>

                <fieldset class="sa-security-methods">
                  <legend>Forma de confirmação</legend>
                  <p>Marque o aplicativo, o código por e-mail, ou os dois.</p>
                  <label
                    class="sa-security-method"
                    :class="{ 'is-selected': hasSecurityMethod(managerTwoFactorMethod, 'authenticator') }"
                  >
                    <input
                      type="checkbox"
                      :checked="hasSecurityMethod(managerTwoFactorMethod, 'authenticator')"
                      @change="toggleManagerMethod('authenticator', $event.target.checked, $event)"
                    />
                    <span>
                      <strong>Google Authenticator</strong>
                      <small>O gestor escaneia um QR code no aplicativo Google Authenticator.</small>
                    </span>
                  </label>
                  <label
                    class="sa-security-method"
                    :class="{ 'is-selected': hasSecurityMethod(managerTwoFactorMethod, 'email') }"
                  >
                    <input
                      type="checkbox"
                      :checked="hasSecurityMethod(managerTwoFactorMethod, 'email')"
                      @change="toggleManagerMethod('email', $event.target.checked, $event)"
                    />
                    <span>
                      <strong>Código por e-mail</strong>
                      <small>O gestor recebe um código de 6 dígitos por e-mail.</small>
                    </span>
                  </label>
                </fieldset>
              </section>

              <div class="form-group sa-security-limit">
                <div class="form-label">
                  <label for="loginAttemptLimit">Tentativas de login para bloquear o IP</label>
                </div>
                <div class="form-input">
                  <input
                    id="loginAttemptLimit"
                    type="number"
                    min="1"
                    step="1"
                    class="form-control"
                    v-model="loginAttemptLimit"
                    placeholder="Ex.: 10"
                  />
                </div>
                <small class="sa-field-hint">
                  Depois desse número de tentativas do mesmo usuário, o IP deixa de conseguir entrar. Vazio não bloqueia.
                </small>
              </div>
            </div>

            <div class="actionsSave">
              <button class="btnSave btnCancel" @click="$router.push('/admin/empresas')" :disabled="!hasReady">
                Voltar
              </button>
              <button class="btnSave" @click="saveCompanyStep1" :disabled="!hasReady">
                <span v-if="hasReady">Salvar</span>
                <span v-else>Salvando...</span>
              </button>
            </div>
          </div>

          <div class="super-admin-page__panel super-admin-page__panel--tabbed" v-if="hasReady && activeStep === 2 && id">
            <CompanyStep2Form
              :company-id="id"
              :type-system="typeSystem"
              :type-business="typeBusiness"
              :representative-id="representativeId"
              :initial-website="websiteData"
              :initial-company="step2CompanyData"
              :company-color="color"
              :redirect-on-save="false"
              :show-cancel="true"
              :show-fill-preview="true"
              :sync-step-query="false"
              finalize-label="Salvar"
              :notify-on-save="false"
              @cancel="setActiveStep(1)"
              @saved="onStep2Saved"
            />
          </div>
        </div>
      </div>
    </div>
    <SaConfirmModal
      v-model="showLayoutSavedModal"
      title="Layout salvo"
      confirm-label="OK"
      max-width="420px"
    >
      <p>Layout da empresa salvo com sucesso.</p>
    </SaConfirmModal>

    <SaConfirmModal
      v-model="showBankConfirmModal"
      title="Atualizar dados bancários?"
      cancel-label="Cancelar"
      confirm-label="Confirmar"
      persistent
      @confirm="confirmBankChange"
    >
      <p>Você alterou dados bancários com recebedor Pagar.me já criado.</p>
      <p>
        A troca só funciona se a conta for do <strong>mesmo CNPJ</strong> da empresa.
        Conta de outro CPF/CNPJ será rejeitada.
      </p>
      <p>Confirma o envio da atualização bancária à Pagar.me?</p>
    </SaConfirmModal>

    <Loader :hasReady="hasReady"/>
  </SuperAdminShell>
</template>

<script>
import SuperAdminShell from '@/Views/Weflix/SuperAdminShell.vue'
import CompanyStep2Form from '@/components/Weflix/SuperAdmin/CompanyStep2Form.vue'
import CompanyProgressPanel from '@/components/Weflix/SuperAdmin/CompanyProgressPanel.vue'
import { mapState } from 'vuex'
import { toast } from 'vue3-toastify'
import superAdmin from '@/utils/superAdmin';
import companySecurityApi, { securityToForm } from '@/utils/companySecurity';
import {
  validatePagarmeRequired,
  validateWeeducCommerceRequired,
  bankSnapshot,
  kycSnapshot,
  representativeSnapshot,
  fieldTip as fieldTipFn,
} from '@/utils/superAdmin/companyFormRules'
import Loader from '@/components/Weflix/Loader.vue'
import SaConfirmModal from '@/components/Weflix/SuperAdmin/SaConfirmModal.vue'

function extractWebsite(source) {
  if (!source || typeof source !== 'object') return null
  if (source.website) return source.website
  if (Array.isArray(source.listWebsite) && source.listWebsite[0]) return source.listWebsite[0]
  if (Array.isArray(source.websites) && source.websites[0]) return source.websites[0]
  return null
}


function emptyOverview() {
  return {
    isMock: false,
    companyName: '',
    companyId: null,
    metrics: { students: 0, courses: 0, sales: 0, revenue: 0, isMock: false },
    checklist: { items: [], doneCount: 0, total: 0, percent: 0 },
    charts: {
      months: [],
      revenueTrend: [],
      salesTrend: [],
      studentsTrend: [],
    },
  }
}

export default {
  data() {
    return {
      hasReady: false,
      showLayoutSavedModal: false,
      showBankConfirmModal: false,
      bankChangeConfirmed: false,
      activeStep: 0,
      id: null,
      tradeName: null,
      name: null,
      idFiscal: null,
      email: null,
      phone: null,
      postcode: null,
      address: null,
      addressNumber: null,
      addressComplement: null,
      neighborhood: null,
      city: null,
      county: null,
      country: null,
      typeSystem: null,
      typeBusiness: null,
      testPlan: null,
      cartStatus: null,
      color: null,
      bank: null,
      branch: null,
      digitBranch: null,
      account: null,
      digitAccount: null,
      videoTimeTotal: null,
      videoTimeUsed: null,
      percentualClient: null,
      transactionValue: null,
      anticipation: 0,
      videoComments: 0,
      intallmentCheckout: 0,
      ageRestricted: 0,
      hasSales: '',
      imagePath: null,
      imageFinalPath: null,
      idFolderVideoteca: null,
      tokenVideoteca: '',
      recipientCode: null,
      split: null,
      representative: null,
      videoteca: null,
      retryingPagarme: false,
      retryingVideoteca: false,
      representativeId: null,
      nameRepresentative: '',
      emailRepresentative: '',
      documentRepresentative: '',
      birthdateRepresentative: '',
      occupationRepresentative: '',
      initialBankSnapshot: '',
      initialKycSnapshot: '',
      initialRepresentativeSnapshot: '',
      websiteData: null,
      companyContents: [],
      companySocialMedia: [],
      companyTestimonials: [],
      companyOverview: emptyOverview(),
      studentTwoFactorRequired: false,
      emailLinkLogin: false,
      twoFactorMethod: 'email',
      managerTwoFactorRequired: true,
      managerTwoFactorMethod: 'email',
      securityRoleTab: 'student',
      loginAttemptLimit: '',
    }
  },

  components: {
    SuperAdminShell,
    CompanyStep2Form,
    CompanyProgressPanel,
    Loader,
    SaConfirmModal,
  },

  computed: {
    ...mapState(['token','currentCompanyCreated']),
    hasPagarmeRecipient() {
      return Boolean(this.recipientCode && String(this.recipientCode).trim())
    },
    kycLocked() {
      return this.hasPagarmeRecipient
    },
    step2CompanyData() {
      return {
        name: this.name,
        tradeName: this.tradeName,
        cartStatus: this.cartStatus,
        intallmentCheckout: this.intallmentCheckout,
        contents: this.companyContents,
        socialMedia: this.companySocialMedia,
        testimonials: this.companyTestimonials,
      };
    },
    videotecaFolderLabel() {
      const status = superAdmin.normalizeVideotecaStatus(this.videoteca, {
        idFolderVideoteca: this.idFolderVideoteca,
      })
      if (status.skipEnabled) return 'criação ignorada neste ambiente'
      if (status.folderId && status.ok) return status.folderId
      if (this.videoteca?.ok === false) return this.videoteca.message || 'falhou'
      return 'sem pasta'
    },
    videotecaTokenLabel() {
      const status = superAdmin.normalizeVideotecaStatus(this.videoteca, {
        idFolderVideoteca: this.idFolderVideoteca,
      })
      if (status.skipEnabled) return 'n/a'
      return status.hasToken ? 'configurado' : 'não configurado'
    },
  },

  methods: {
    fieldTip(key) {
      return fieldTipFn(key, { kycLocked: this.kycLocked })
    },
    setActiveStep(step) {
      this.activeStep = step
      const query = { ...this.$route.query }
      delete query.step
      delete query.tab
      if (step === 1) query.step = '1'
      else if (step === 2) query.tab = 'layout'
      this.$router.replace({ query }).catch(() => {})
    },
    resolveActiveStepFromRoute() {
      const tab = String(this.$route.query.tab || '').toLowerCase()
      const step = Number(this.$route.query.step)
      let stored = ''
      try {
        stored = sessionStorage.getItem('saEditTab') || ''
        if (stored) sessionStorage.removeItem('saEditTab')
      } catch (_) { /* ignore */ }

      if (tab === 'layout' || stored === 'layout' || step === 2 || step === 3) {
        this.activeStep = 2
      } else if (tab === 'dados' || step === 1) {
        this.activeStep = 1
      } else {
        this.activeStep = 0
      }
    },
    onStep2Saved() {
      this.showLayoutSavedModal = true
      this.refreshCompanyOverview()
    },
    companySnapshot() {
      return {
        id: this.id,
        name: this.name,
        tradeName: this.tradeName,
        email: this.email,
        phone: this.phone,
        idFiscal: this.idFiscal,
        postcode: this.postcode,
        city: this.city,
        cartStatus: this.cartStatus,
        typeSystem: this.typeSystem,
        typeBusiness: this.typeBusiness,
      }
    },
    async refreshCompanyOverview() {
      const companyId = this.id || Number(this.$route.params.slug)
      const company = this.companySnapshot()
      if (!companyId) {
        this.companyOverview = superAdmin.buildCompanyOverview(
          company,
          this.websiteData,
          this.videoteca,
        )
        return
      }
      try {
        const [overview, videotecaStatus] = await Promise.all([
          superAdmin.getCompanyOverview(this.token, companyId),
          this.refreshVideotecaStatus(companyId),
        ])
        const checklist = superAdmin.mergeVideotecaIntoChecklist(
          overview?.checklist || {},
          this.videoteca,
          { idFolderVideoteca: this.idFolderVideoteca },
        )
        this.companyOverview = {
          ...emptyOverview(),
          ...overview,
          isMock: !!overview?.isMock,
          metrics: { ...emptyOverview().metrics, ...(overview.metrics || {}), isMock: !!overview?.isMock },
          checklist,
          charts: { ...emptyOverview().charts, ...(overview.charts || {}) },
        }
      } catch (error) {
        console.error('Erro ao carregar overview da empresa:', error)
        this.companyOverview = superAdmin.buildCompanyOverview(
          company,
          this.websiteData,
          this.videoteca,
        )
      }
    },
    async refreshVideotecaStatus(companyId = this.id) {
      if (!companyId || !this.token) return this.videoteca
      try {
        const status = await superAdmin.getVideoteca(this.token, companyId)
        this.videoteca = {
          ...(this.videoteca || {}),
          ...status,
        }
        if (status?.folderId) {
          this.idFolderVideoteca = status.folderId
        } else if (status?.idFolderVideoteca) {
          this.idFolderVideoteca = status.idFolderVideoteca
        }
        return this.videoteca
      } catch (error) {
        console.warn('Não foi possível carregar status da Videoteca:', error)
        return this.videoteca
      }
    },
    async getCep() {
      const cep = String(this.postcode || '').replace(/\D/g, '')
      if (cep.length === 8) {
        try {
          const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`)
          const data = await response.json()
          if (!data.erro) {
            this.address = data.logradouro
            this.neighborhood = data.bairro
            this.city = data.localidade
            this.county = data.uf
            this.country = 'Brasil'
          }
        } catch (error) {
          console.error('Erro ao buscar CEP:', error)
        }
      }
    },

    onSalesIntentChange() {
      if (String(this.hasSales) === '1') {
        if (Number(this.cartStatus) === 2) this.cartStatus = 1
        return
      }
      if (String(this.hasSales) !== '0') return
      this.split = ''
      this.bank = ''
      this.branch = ''
      this.digitBranch = ''
      this.account = ''
      this.digitAccount = ''
      this.transactionValue = ''
      this.percentualClient = ''
      this.cartStatus = 2
      this.anticipation = 0
      this.intallmentCheckout = 0
    },

    formForPagarmeValidation() {
      return {
        tradeName: this.tradeName,
        name: this.name,
        idFiscal: this.idFiscal,
        email: this.email,
        phone: this.phone,
        postcode: this.postcode,
        address: this.address,
        addressNumber: this.addressNumber,
        neighborhood: this.neighborhood,
        city: this.city,
        county: this.county,
        bank: this.bank,
        branch: this.branch,
        account: this.account,
        digitAccount: this.digitAccount,
        nameRepresentative: this.nameRepresentative,
        emailRepresentative: this.emailRepresentative,
        documentRepresentative: this.documentRepresentative,
        birthdateRepresentative: this.birthdateRepresentative,
        occupationRepresentative: this.occupationRepresentative,
        split: this.split,
        percentualClient: this.percentualClient,
        transactionValue: this.transactionValue,
        hasSales: this.hasSales,
      }
    },
    captureFormSnapshots() {
      const form = this.formForPagarmeValidation()
      this.initialBankSnapshot = bankSnapshot({
        ...form,
        digitBranch: this.digitBranch,
      })
      this.initialKycSnapshot = kycSnapshot(form)
      this.initialRepresentativeSnapshot = representativeSnapshot(form)
    },
    formatBirthdateForInput(value) {
      if (!value) return ''
      const raw = String(value).trim()
      if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 10)
      const br = raw.match(/^(\d{2})\/(\d{2})\/(\d{4})$/)
      if (br) return `${br[3]}-${br[2]}-${br[1]}`
      return ''
    },
    buildRepresentativePayload() {
      const document = String(this.documentRepresentative || '').replace(/\D/g, '')
      const birthdate = this.formatBirthdateForInput(this.birthdateRepresentative) || null
      const representative = {
        name: (this.nameRepresentative || '').trim() || null,
        email: (this.emailRepresentative || '').trim() || null,
        document: document || null,
        birthdate,
        professional_occupation: (this.occupationRepresentative || '').trim() || null,
      }
      if (this.representativeId) {
        representative.id = this.representativeId
      }
      return representative
    },
    confirmBankChange() {
      this.showBankConfirmModal = false
      this.bankChangeConfirmed = true
      this.saveCompanyStep1()
    },
    async saveCompanyStep1() {
      const form = this.formForPagarmeValidation()
      const commerce = validateWeeducCommerceRequired(form)
      if (!commerce.ok) {
        window.alert(
          `Split, % do cliente e taxa são obrigatórios no Weeduc (sem isso as compras não passam):\n\n• ${commerce.errors.join('\n• ')}`,
        )
        return
      }

      const rawLimit = String(this.loginAttemptLimit ?? '').trim()
      if (rawLimit !== '' && !/^[1-9]\d*$/.test(rawLimit)) {
        toast.error('Informe um número inteiro maior que zero no limite de tentativas, ou deixe vazio.')
        return
      }

      const validation = validatePagarmeRequired(form)
      const willSell = String(this.hasSales) === '1'
      const bankChanged =
        willSell
        && this.hasPagarmeRecipient
        && bankSnapshot({ ...form, digitBranch: this.digitBranch }) !== this.initialBankSnapshot
      const wantsPagarmeSync = willSell && Boolean(
        this.bank && this.branch && this.account && this.digitAccount,
      )

      if (!this.hasPagarmeRecipient && wantsPagarmeSync && !validation.ok) {
        const missing = validation.errors.join('\n• ')
        const proceed = window.confirm(
          `Faltam dados obrigatórios para criar o recebedor Pagar.me:\n\n• ${missing}\n\n`
          + 'Deseja salvar só no Weeduc sem criar o recebedor agora?',
        )
        if (!proceed) return
      }

      if (bankChanged && !this.bankChangeConfirmed) {
        this.showBankConfirmModal = true
        return
      }
      this.bankChangeConfirmed = false

      this.hasReady = false
      const representative = this.buildRepresentativePayload()
      const representativeChanged =
        representativeSnapshot(form) !== this.initialRepresentativeSnapshot
      if (
        willSell
        && (!this.hasPagarmeRecipient || representativeChanged)
        && (!representative.birthdate || !representative.professional_occupation)
      ) {
        this.hasReady = true
        toast.warning(
          'Preencha a data de nascimento e a profissão do responsável antes de salvar.',
        )
        return
      }
      const isWeeducCorporate = String(this.typeSystem) === '1' && String(this.typeBusiness) === '2'
      const payload = {
        id: this.id,
        idFiscal: this.idFiscal?.replace(/\D/g, ''),
        name: this.name,
        country: this.country,
        county: this.county,
        city: this.city,
        address: this.address,
        addressNumber: this.addressNumber,
        addressComplement: String(this.addressComplement || '').trim() || 'Sem complemento',
        postcode: this.postcode?.replace(/\D/g, ''),
        testPlan: this.testPlan,
        typeSystem: this.typeSystem,
        ...(String(this.typeSystem) === '1'
          ? { typeBusiness: Number(this.typeBusiness) || 1 }
          : {}),
        anticipation: willSell ? this.anticipation : 0,
        videoComments: this.videoComments,
        cartStatus: willSell ? this.cartStatus : 2,
        intallmentCheckout: willSell ? this.intallmentCheckout : 0,
        ageRestricted: Number(this.ageRestricted) || 0,
        color: this.color,
        bank: willSell ? this.bank : null,
        branch: willSell ? this.branch : null,
        digitBranch: willSell ? this.digitBranch : null,
        account: willSell ? this.account : null,
        digitAccount: willSell ? this.digitAccount : null,
        videoTimeTotal: this.videoTimeTotal,
        videoTimeUsed: this.videoTimeUsed,
        idFolderVideoteca: this.idFolderVideoteca,
        tradeName: this.tradeName,
        email: this.email,
        phone: this.phone?.replace(/\D/g, ''),
        percentualClient: willSell ? this.percentualClient : null,
        transactionValue: willSell ? this.transactionValue : null,
        recipientCode: this.recipientCode,
        neighborhood: this.neighborhood,
        split: willSell ? this.split : null,
        corporateCompany: isWeeducCorporate,
        isCorporate: isWeeducCorporate,
      }
      // PUT /admin/companies/{id} só aceita Company — representative é ignorado.
      // Data/profissão só persistem via PUT .../pagarme/recipient { company, representative }.
      try {
        await superAdmin.updateCompany(this.token, this.id, payload)

        const kycChanged = kycSnapshot(form) !== this.initialKycSnapshot
        const shouldSyncRecipient = willSell && (
          (
            !this.kycLocked
            && (
              (wantsPagarmeSync && validation.ok && (!this.hasPagarmeRecipient || bankChanged))
              || kycChanged
              || Boolean(representative.birthdate || representative.professional_occupation)
            )
          )
          || representativeChanged
        )

        if (shouldSyncRecipient) {
          try {
            const recipientResponse = await superAdmin.updatePagarmeRecipient(
              this.token,
              this.id,
              { company: payload, representative },
            )
            if (recipientResponse?.representative?.id) {
              this.representativeId = recipientResponse.representative.id
            }
            if (recipientResponse?.representative) {
              this.representative = recipientResponse.representative
              this.birthdateRepresentative = this.formatBirthdateForInput(
                recipientResponse.representative.birthdate,
              )
              this.occupationRepresentative =
                recipientResponse.representative.professional_occupation || ''
            } else {
              this.representative = representative
            }
            if (recipientResponse?.company?.recipientCode) {
              this.recipientCode = recipientResponse.company.recipientCode
            } else if (recipientResponse?.recipient?.id) {
              this.recipientCode = recipientResponse.recipient.id
            }
            if (recipientResponse?.pagarmeError) {
              toast.warning(
                `Empresa salva, mas o recebedor Pagar.me falhou: ${recipientResponse.pagarmeError}`,
              )
            }
          } catch (pagarmeError) {
            console.warn('Atualização do recebedor / responsável falhou:', pagarmeError)
            toast.warning(
              'Empresa salva, mas data de nascimento e profissão do responsável não puderam ser atualizadas.',
            )
          }
        } else if (wantsPagarmeSync && !validation.ok && this.hasPagarmeRecipient) {
          toast.info('Empresa salva no Weeduc. Dados KYC bloqueados; envio à Pagar.me ignorado.')
        } else {
          this.representative = representative
        }

        const tokenVideoteca = String(this.tokenVideoteca || '').trim()
        if (tokenVideoteca) {
          try {
            const linked = await superAdmin.linkVideotecaAccount(this.token, this.id, {
              tokenVideoteca,
            })
            this.videoteca = {
              ...(this.videoteca || {}),
              ...linked,
              hasToken: true,
            }
            this.tokenVideoteca = ''
          } catch (videotecaError) {
            console.warn('Vínculo do token Videoteca falhou:', videotecaError)
            toast.warning(
              'Empresa salva, mas o token da Videoteca não pôde ser vinculado.',
            )
          }
        }

        this.captureFormSnapshots()
        try {
          await this.persistSecurity()
        } catch (securityError) {
          console.warn('Regras de segurança não puderam ser gravadas:', securityError)
          toast.warning('Empresa salva, mas as regras de segurança não puderam ser gravadas.')
        }
        this.hasReady = true
        await this.refreshCompanyOverview()
        toast.success('Dados da empresa salvos com sucesso.')
      } catch (error) {
        console.error(error)
        this.hasReady = true
        toast.error('Erro ao salvar a empresa. Tente novamente.')
      }
    },
    securityMethod(value) {
      if (value === 'authenticator' || value === 'both') return value
      return 'email'
    },
    hasSecurityMethod(stored, method) {
      return stored === 'both' || stored === method
    },
    nextSecurityMethod(stored, method, checked) {
      const email = method === 'email' ? checked : this.hasSecurityMethod(stored, 'email')
      const authenticator = method === 'authenticator' ? checked : this.hasSecurityMethod(stored, 'authenticator')
      if (email && authenticator) return 'both'
      if (authenticator) return 'authenticator'
      if (email) return 'email'
      return stored
    },
    toggleStudentMethod(method, checked, event) {
      const next = this.nextSecurityMethod(this.twoFactorMethod, method, checked)
      if (next === this.twoFactorMethod && event?.target) {
        event.target.checked = this.hasSecurityMethod(this.twoFactorMethod, method)
      }
      this.twoFactorMethod = next
    },
    toggleManagerMethod(method, checked, event) {
      const next = this.nextSecurityMethod(this.managerTwoFactorMethod, method, checked)
      if (next === this.managerTwoFactorMethod && event?.target) {
        event.target.checked = this.hasSecurityMethod(this.managerTwoFactorMethod, method)
      }
      this.managerTwoFactorMethod = next
    },
    setStudentTwoFactor(enabled) {
      this.studentTwoFactorRequired = enabled
      if (!enabled) this.twoFactorMethod = 'email'
    },
    applySecurity(security) {
      const next = securityToForm(security)
      this.studentTwoFactorRequired = next.studentTwoFactorRequired
      this.emailLinkLogin = next.emailLinkLogin
      this.twoFactorMethod = this.securityMethod(next.studentTwoFactorMethod || next.twoFactorMethod)
      this.managerTwoFactorRequired = true
      this.managerTwoFactorMethod = this.securityMethod(next.managerTwoFactorMethod)
      this.loginAttemptLimit = next.loginAttemptLimit
    },
    async persistSecurity() {
      if (!this.id) return
      const rawLimit = String(this.loginAttemptLimit ?? '').trim()
      if (rawLimit !== '' && !/^[1-9]\d*$/.test(rawLimit)) {
        throw new Error('invalid-login-attempt-limit')
      }
      const studentMethod = this.studentTwoFactorRequired
        ? this.securityMethod(this.twoFactorMethod)
        : 'email'
      const managerMethod = this.securityMethod(this.managerTwoFactorMethod)
      const saved = await companySecurityApi.updateForCompany(this.token, this.id, {
        studentTwoFactorRequired: this.studentTwoFactorRequired,
        studentTwoFactorMethod: studentMethod,
        managerTwoFactorRequired: true,
        managerTwoFactorMethod: managerMethod,
        emailLinkLogin: this.emailLinkLogin,
        twoFactorRequired: this.studentTwoFactorRequired,
        twoFactorMethod: studentMethod,
        loginAttemptLimit: rawLimit,
      })
      this.applySecurity(saved)
    },
    async loadSecurity() {
      if (!this.id) return
      try {
        const security = await companySecurityApi.getForCompany(this.token, this.id)
        this.applySecurity(security)
      } catch (error) {
        console.warn('Falha ao carregar segurança da empresa:', error)
        this.applySecurity(null)
      }
    },
    async getCompany() {
    const id = this.$route.params.slug
        
    try {
      const response = await superAdmin.createCompanyById(this.token, id)
      this.id = response.id
      this.idFiscal = response.idFiscal
      this.name = response.name
      this.country = response.country
      this.county = response.county
      this.city = response.city
      this.address = response.address
      this.addressNumber = response.addressNumber
      this.addressComplement = response.addressComplement
      this.postcode = response.postcode
      this.testPlan = Number(response.testPlan) ? 1 : 0
      this.typeSystem = response.typeSystem
      this.typeBusiness = response.typeBusiness ?? null
      this.anticipation = Number(response.anticipation) ? 1 : 0
      this.videoComments = Number(response.videoComments) ? 1 : 0
      this.cartStatus = response.cartStatus
      this.intallmentCheckout = Number(response.intallmentCheckout) ? 1 : 0
      this.ageRestricted = Number(response.ageRestricted) ? 1 : 0
      this.color = response.color
      this.bank = response.bank
      this.branch = response.branch
      this.digitBranch = response.digitBranch
      this.account = response.account
      this.digitAccount = response.digitAccount
      this.videoTimeTotal = response.videoTimeTotal
      this.videoTimeUsed = response.videoTimeUsed
      this.idFolderVideoteca = response.idFolderVideoteca
      this.tokenVideoteca = ''
      this.tradeName = response.tradeName
      this.email = response.email
      this.phone = response.phone
      this.percentualClient = response.percentualClient
      this.transactionValue = response.transactionValue
      this.recipientCode = response.recipientCode
      this.neighborhood = response.neighborhood
      this.split = response.split
      if (Number(response.cartStatus) === 2) {
        this.hasSales = '0'
      } else {
        const hasPaymentData = [response.bank, response.branch, response.account, response.percentualClient, response.transactionValue]
          .some((value) => value != null && String(value).trim() !== '')
        this.hasSales = hasPaymentData || Number(response.split) === 1 || Number(response.cartStatus) === 1 ? '1' : '0'
      }
      this.representative = response.representative || null
      this.representativeId = response.representative?.id
        || response.idRepresentative
        || response.representativeId
        || null
      this.nameRepresentative = response.representative?.name || ''
      this.emailRepresentative = response.representative?.email || ''
      this.documentRepresentative = response.representative?.document || ''
      this.birthdateRepresentative = this.formatBirthdateForInput(
        response.representative?.birthdate,
      )
      this.occupationRepresentative =
        response.representative?.professional_occupation || ''
      this.videoteca = response.videoteca || null
      this.websiteData = extractWebsite(response)
      this.companyContents = Array.isArray(response?.contents) ? response.contents : []
      this.companySocialMedia = Array.isArray(response?.socialMedia) ? response.socialMedia : []
      this.companyTestimonials = Array.isArray(response?.testimonials) ? response.testimonials : []
      this.captureFormSnapshots()
      await this.loadSecurity()
      await this.refreshCompanyOverview()
      this.hasReady = true
    } catch (error) {
      console.error('Erro ao carregar dados da empresa:', error)
      this.applySecurity(null)
      this.name = this.name || 'Empresa Preview'
      this.tradeName = this.tradeName || 'Empresa Preview'
      this.id = this.id || Number(id) || null
      await this.refreshCompanyOverview()
      this.hasReady = true
      }
    },
    async retryRecipient() {
      if (!this.id || this.retryingPagarme) return
      const validation = validatePagarmeRequired(this.formForPagarmeValidation())
      if (!validation.ok) {
        window.alert(
          `Para criar/atualizar o recebedor Pagar.me, complete:\n\n• ${validation.errors.join('\n• ')}`,
        )
        return
      }
      if (this.hasPagarmeRecipient) {
        const ok = window.confirm(
          'Atualizar o recebedor Pagar.me?\n\n'
          + 'KYC já preenchido quase nunca muda. Conta bancária só é aceita do mesmo CNPJ.',
        )
        if (!ok) return
      } else {
        const ok = window.confirm(
          'Criar o recebedor Pagar.me agora?\n\n'
          + 'Após criado, CNPJ, razão social, e-mail, telefone, endereço e responsável '
          + 'ficam praticamente imutáveis. Para outro CNPJ, será preciso outra empresa.',
        )
        if (!ok) return
      }
      this.retryingPagarme = true
      try {
        const payload = {
          company: {
            id: this.id,
            idFiscal: this.idFiscal?.replace(/\D/g, ''),
            name: this.name,
            tradeName: this.tradeName,
            email: this.email,
            phone: this.phone?.replace(/\D/g, ''),
            bank: this.bank,
            branch: this.branch,
            digitBranch: this.digitBranch,
            account: this.account,
            digitAccount: this.digitAccount,
            address: this.address,
            addressNumber: this.addressNumber,
            addressComplement: this.addressComplement || 'Sem complemento',
            neighborhood: this.neighborhood,
            city: this.city,
            county: this.county,
            postcode: this.postcode?.replace(/\D/g, ''),
          },
          representative: this.buildRepresentativePayload(),
        }
        const response = this.recipientCode
          ? await superAdmin.updatePagarmeRecipient(this.token, this.id, payload)
          : await superAdmin.retryPagarmeRecipient(this.token, this.id)
        if (response?.recipient?.id) {
          this.recipientCode = response.recipient.id
        } else if (response?.company?.recipientCode) {
          this.recipientCode = response.company.recipientCode
        }
        this.captureFormSnapshots()
        if (response?.pagarmeError) {
          window.alert(response.pagarmeError)
        } else {
          window.alert(this.recipientCode ? 'Recebedor Pagar.me atualizado.' : 'Recebedor Pagar.me criado.')
        }
      } catch (error) {
        console.error(error)
        const message = error?.response?.data?.message || error?.message || 'Erro ao falar com a Pagar.me.'
        window.alert(message)
      } finally {
        this.retryingPagarme = false
      }
    },
    async retryVideoteca() {
      if (!this.id || this.retryingVideoteca) return
      this.retryingVideoteca = true
      try {
        const response = await superAdmin.createVideotecaFolder(this.token, this.id)
        this.videoteca = {
          ...(this.videoteca || {}),
          ...response,
        }
        if (response?.folderId) {
          this.idFolderVideoteca = response.folderId
        }
        await this.refreshVideotecaStatus()
        await this.refreshCompanyOverview()
        if (response?.ok === false) {
          window.alert(response.message || 'Não foi possível criar a pasta da Videoteca.')
        } else {
          window.alert(response?.skipEnabled
            ? (response.message || 'Criação da pasta ignorada neste ambiente.')
            : 'Pasta da Videoteca criada.')
        }
      } catch (error) {
        console.error(error)
        window.alert(error?.response?.data?.message || 'Erro ao criar a pasta da Videoteca.')
      } finally {
        this.retryingVideoteca = false
      }
    }
  },
  created() {
    this.resolveActiveStepFromRoute()
    this.getCompany()
  },
  watch: {
    '$route.query': {
      deep: true,
      handler() {
        this.resolveActiveStepFromRoute()
      },
    },
  },
}
</script>

<style scoped>
.sa-company-integrations {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem 1rem;
  margin: 0 0 1.25rem;
  padding: 0.85rem 1rem;
  border: 1px solid var(--dashboard-border, #e8eaf0);
  border-radius: 10px;
  background: #f8f9fc;
}
.sa-company-integrations__status {
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  font-size: 0.8125rem;
  color: var(--dashboard-muted, #697386);
}
.sa-company-integrations__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}
.sa-field-hint {
  display: block;
  margin-top: 0.3rem;
  font-size: 0.75rem;
  color: #6b7280;
}

.sa-company-security {
  margin-top: 1.75rem;
  padding-top: 1.25rem;
  border-top: 1px solid #e7eaf0;
  text-align: left;
}
.sa-security-tabs {
  display: inline-flex;
  gap: 0.25rem;
  margin: 0 0 1rem;
  padding: 0.25rem;
  border-radius: 12px;
  background: #f3f4f8;
}
.sa-security-tabs__tab {
  border: 0;
  border-radius: 9px;
  padding: 0.45rem 1rem;
  background: transparent;
  color: #5c6370;
  font-size: 0.9rem;
  font-weight: 700;
  cursor: pointer;
}
.sa-security-tabs__tab.is-active {
  background: #fff;
  color: #160266;
  box-shadow: 0 1px 2px rgba(22, 2, 102, 0.08);
}
.sa-security-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}
.sa-security-switch.is-locked {
  cursor: not-allowed;
  pointer-events: none;
}
.sa-security-switch.is-locked input:checked + .sa-security-switch__track {
  background: #c5c8d0;
}
.sa-security-switch.is-locked .sa-security-switch__text {
  color: #9aa0ab;
}
.sa-security-row {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: center;
  padding: 1rem 1.1rem;
  border: 1px solid #f0f2f5;
  border-radius: 12px;
  background: #fafbfc;
}
.sa-security-row strong {
  display: block;
  color: #1a1d26;
  font-size: 0.95rem;
}
.sa-security-row p,
.sa-security-methods > p {
  margin: 0.25rem 0 0;
  color: #5c6370;
  font-size: 0.85rem;
  line-height: 1.45;
}
.sa-security-switch {
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  flex-shrink: 0;
  cursor: pointer;
}
.sa-security-switch input {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}
.sa-security-switch__track {
  position: relative;
  width: 42px;
  height: 24px;
  border-radius: 999px;
  background: #d5d8e0;
}
.sa-security-switch__track::after {
  content: '';
  position: absolute;
  top: 3px;
  left: 3px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #fff;
  transition: transform 0.15s ease;
}
.sa-security-switch input:checked + .sa-security-switch__track {
  background: #160266;
}
.sa-security-switch input:checked + .sa-security-switch__track::after {
  transform: translateX(18px);
}
.sa-security-switch__text {
  min-width: 3.2rem;
  color: #5c6370;
  font-size: 0.85rem;
  font-weight: 600;
}
.sa-security-methods {
  margin: 1rem 0 0;
  padding: 0;
  border: 0;
}
.sa-security-methods legend {
  margin-bottom: 0.35rem;
  color: #1a1d26;
  font-size: 0.95rem;
  font-weight: 700;
}
.sa-security-method {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin-top: 0.75rem;
  padding: 1rem 1.1rem;
  border: 1px solid #f0f2f5;
  border-radius: 12px;
  background: #fafbfc;
  cursor: pointer;
}
.sa-security-method.is-selected {
  border-color: #160266;
  background: #f7f5fc;
}
.sa-security-method input {
  margin-top: 0.2rem;
  accent-color: #160266;
}
.sa-security-method strong {
  display: block;
  color: #1a1d26;
  font-size: 0.95rem;
}
.sa-security-method small {
  display: block;
  margin-top: 0.25rem;
  color: #5c6370;
  font-size: 0.85rem;
  line-height: 1.45;
}
.sa-security-limit {
  max-width: 28rem;
  margin-top: 1rem;
}

.section-title .sa-field-subtle {
  margin: 0.5rem 0 0;
  font-size: 0.8125rem;
  color: #697386;
  line-height: 1.4;
  text-align: left;
  width: 100%;
}
.sa-field-subtle :deep(svg) {
  width: 0.75rem;
  height: 0.75rem;
  margin: 0 0.15rem;
  vertical-align: -0.05rem;
  opacity: 0.7;
}
</style>

